import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Hourglass, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props<T extends { id: string }> {
  /** Server total of expired videos. */
  count: number;
  /** i18n key with a {{count}}, e.g. "library.expiredGroup". */
  labelKey: string;
  pageSize: number;
  /** Loads one page of expired videos (only once the group is opened). */
  loadPage: (offset: number) => Promise<T[]>;
  renderRow: (item: T, remove: (id: string) => void) => ReactNode;
}

/**
 * The expired videos, closed at the end of the "Todos" list: they used to fill
 * the list as full-size faded cards (63 of one user's newest 100 libraries).
 */
export function ListExpiredGroup<T extends { id: string }>({
  count, labelKey, pageSize, loadPage, renderRow,
}: Props<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<T[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = async (offset: number) => {
    setLoading(true);
    setFailed(false);
    try {
      const page = await loadPage(offset);
      setItems((current) => (offset === 0 ? page : [...current, ...page]));
      setHasMore(page.length >= pageSize);
      setLoaded(true);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) void load(0);
  };

  const remove = (id: string) => setItems((current) => current.filter((item) => item.id !== id));

  return (
    <section data-testid="list-expired-group" className="rounded-xl border border-dashed border-border">
      <button
        type="button"
        data-testid="list-expired-toggle"
        aria-expanded={open}
        onClick={toggle}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm"
      >
        <Hourglass className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold">{t(labelKey, { count })}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-[13px] font-semibold text-primary">
          {open ? t('lists.hide') : t('lists.show')}
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open && (
        <div className="border-t border-border px-4 pb-2">
          {items.map((item) => renderRow(item, remove))}
          {loading && (
            <div className="flex justify-center py-3 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
          {failed && (
            <button
              type="button"
              className="w-full py-3 text-center text-[13px] text-destructive"
              onClick={() => void load(items.length)}
            >
              {t('lists.loadFailedRetry')}
            </button>
          )}
          {!loading && !failed && hasMore && loaded && (
            <button
              type="button"
              className="w-full py-3 text-center text-[13px] font-semibold text-primary"
              onClick={() => void load(items.length)}
            >
              {t('lists.showMore')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
